import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { PhoneIcon, ArrowRightIcon } from "lucide-react";
import { Link } from "react-router-dom";

interface AuthFormProps {
  mode: "login" | "register";
  onSubmit: (data: { phone: string }) => void;
  initialPhone?: string;
  isLoading?: boolean;
}

export default function AuthForm({
  mode,
  onSubmit,
  initialPhone,
  isLoading = false,
}: AuthFormProps) {
  const [phone, setPhone] = useState(initialPhone || "");
  const [phoneError, setPhoneError] = useState("");

  const validatePhone = (value: string) => {
    if (!value) {
      setPhoneError("Phone number is required");
      return false;
    }

    // Remove all spaces and non-numeric characters except the + sign
    const cleanedValue = value.replace(/\s+/g, "");
    
    // Indian phone number validation
    // Format 1: 10 digits (e.g., 9876543210)
    // Format 2: +91 followed by 10 digits (e.g., +919876543210)
    const indianPhoneRegex = /^(\+91)?[6-9]\d{9}$/;
    
    if (!indianPhoneRegex.test(cleanedValue)) {
      setPhoneError("Please enter a valid Indian phone number");
      return false;
    }

    setPhoneError("");
    return true;
  };



  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (validatePhone(phone)) {
      onSubmit({ phone });
    }
  };

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader>
        <CardTitle className="text-2xl font-bold">
          {mode === "login" ? "Login to Your Account" : "Create Your Account"}
        </CardTitle>
        <CardDescription>
          {mode === "login"
            ? "Enter your details to access your account"
            : "Sign up to start buying and selling machinery"}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit}>
          <div className="mb-6 flex items-center gap-2 justify-center">
            <PhoneIcon className="h-5 w-5 text-primary" />
            <h3 className="text-lg font-medium">Phone Authentication</h3>
          </div>
          
          <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="phone">Phone Number</Label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                    <span className="text-gray-500 dark:text-gray-400">+91</span>
                  </div>
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="9876543210"
                    value={phone}
                    onChange={(e) => {
                      // Remove any non-digit characters from input
                      const value = e.target.value.replace(/[^0-9+]/g, '');
                      setPhone(value);
                      if (phoneError) validatePhone(value);
                    }}
                    className={`pl-12 ${phoneError ? "border-red-500" : ""}`}
                  />
                </div>

                {phoneError && (
                  <p className="text-sm text-red-500">{phoneError}</p>
                )}
                <p className="text-sm text-muted-foreground">
                  We'll send a verification code to this number
                </p>
                <p className="text-xs text-muted-foreground">
                  Only Indian phone numbers (+91) are supported
                </p>
              </div>
            </div>

          <Button type="submit" className="w-full mt-6" disabled={isLoading}>
            {isLoading ? (
              <span className="flex items-center gap-2">
                <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
                <span>Processing...</span>
              </span>
            ) : (
              <span className="flex items-center justify-center gap-2">
                <span>{mode === "login" ? "Continue" : "Create Account"}</span>
                <ArrowRightIcon className="h-4 w-4" />
              </span>
            )}
          </Button>
        </form>
      </CardContent>
      <Separator />
      <CardFooter className="flex justify-center p-6">
        {mode === "login" ? (
          <p className="text-sm text-center">
            Don't have an account?{" "}
            <Link
              to="/register"
              className="font-medium text-primary hover:underline"
            >
              Register
            </Link>
          </p>
        ) : (
          <p className="text-sm text-center">
            Already have an account?{" "}
            <Link
              to="/login"
              className="font-medium text-primary hover:underline"
            >
              Login
            </Link>
          </p>
        )}
      </CardFooter>
    </Card>
  );
}
